import React, { useState, useEffect, useRef } from "react";
import { AdminProject, CreateProjectRequest, UpdateProjectRequest, ProjectImage } from "../../types/admin";
import { projectsApi } from "../../services/api";
import { useToast } from "./Toast";

interface ProjectFormState {
  name: string;
  descriptionPt: string;
  descriptionEn: string;
  techStack: string[];
  viewUrl: string;
  codeUrl: string;
  existingImages: ProjectImage[];
  newFiles: File[];
}

const emptyForm: ProjectFormState = {
  name: "",
  descriptionPt: "",
  descriptionEn: "",
  techStack: [],
  viewUrl: "",
  codeUrl: "",
  existingImages: [],
  newFiles: [],
};

const ProjectsPage: React.FC = () => {
  const { showToast } = useToast();
  const [projects, setProjects] = useState<AdminProject[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [form, setForm] = useState<ProjectFormState>(emptyForm);
  const [newFilePreviews, setNewFilePreviews] = useState<string[]>([]);
  const [techInput, setTechInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProjects();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadProjects = async () => {
    setLoading(true);
    try {
      const response = await projectsApi.getAll();
      if (response.success && response.data) {
        setProjects(response.data);
      } else {
        showToast(response.message || "Failed to load projects", "error");
      }
    } catch {
      showToast("Failed to load projects", "error");
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setNewFilePreviews([]);
    setTechInput("");
    setErrors({});
    setModalOpen(true);
  };

  const openEdit = async (project: AdminProject) => {
    setEditingId(project.id);
    setNewFilePreviews([]);
    setTechInput("");

    let existingImages: ProjectImage[] = [...project.images];
    try {
      const imgRes = await projectsApi.getImages(project.id);
      if (imgRes.success && imgRes.data && imgRes.data.length > 0) {
        existingImages = imgRes.data.map((url) => {
          const segments = url.replace(/\/+$/, "").split("/");
          const id = parseInt(segments[segments.length - 1], 10);
          return { id: isNaN(id) ? 0 : id, url };
        });
      }
    } catch {
      // Fall back to project.images
    }

    setForm({
      name: project.name,
      descriptionPt: project.descriptionPt,
      descriptionEn: project.descriptionEn,
      techStack: [...project.techStack],
      viewUrl: project.viewUrl,
      codeUrl: project.codeUrl,
      existingImages,
      newFiles: [],
    });
    setErrors({});
    setModalOpen(true);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!form.name.trim()) newErrors.name = "Project name is required";
    if (!form.descriptionPt.trim()) newErrors.descriptionPt = "Description (PT) is required";
    if (!form.descriptionEn.trim()) newErrors.descriptionEn = "Description (EN) is required";
    if (form.techStack.length === 0) newErrors.techStack = "At least one technology is required";
    if (!form.viewUrl.trim()) newErrors.viewUrl = "Live URL is required";
    if (!form.codeUrl.trim()) newErrors.codeUrl = "GitHub URL is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setSaving(true);
    try {
      const payload: CreateProjectRequest | UpdateProjectRequest = {
        name: form.name,
        descriptionPt: form.descriptionPt,
        descriptionEn: form.descriptionEn,
        techStack: form.techStack,
        viewUrl: form.viewUrl,
        codeUrl: form.codeUrl,
      };

      let projectId: string | number | null = editingId;
      let successMessage: string | null = null;

      if (editingId) {
        const response = await projectsApi.update(editingId, payload, form.newFiles.length > 0 ? form.newFiles : undefined);
        if (!response.success) {
          showToast(response.message || "Failed to update", "error");
          return;
        }
        successMessage = response.message || null;
      } else {
        const response = await projectsApi.create(payload, form.newFiles.length > 0 ? form.newFiles : undefined);
        if (response.success && response.data) {
          projectId = response.data.id;
          successMessage = response.message || null;
        } else {
          showToast(response.message || "Failed to create", "error");
          return;
        }
      }

      showToast(successMessage || (editingId ? "Project updated successfully" : "Project created successfully"));
      setModalOpen(false);
      loadProjects();
    } catch {
      showToast("An error occurred", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string | number) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      const response = await projectsApi.delete(id);
      if (response.success) {
        showToast(response.message || "Project deleted");
        loadProjects();
      } else {
        showToast(response.message || "Failed to delete", "error");
      }
    } catch {
      showToast("An error occurred", "error");
    }
  };

  const addTech = () => {
    const value = techInput.trim();
    if (value && !form.techStack.includes(value)) {
      setForm((prev) => ({ ...prev, techStack: [...prev.techStack, value] }));
      setTechInput("");
      setErrors((prev) => ({ ...prev, techStack: "" }));
    }
  };

  const removeTech = (tech: string) => {
    setForm((prev) => ({
      ...prev,
      techStack: prev.techStack.filter((t) => t !== tech),
    }));
  };

  const handleTechKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addTech();
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const fileArray = Array.from(files);
    setForm((prev) => ({ ...prev, newFiles: [...prev.newFiles, ...fileArray] }));

    fileArray.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const url = ev.target?.result as string;
        setNewFilePreviews((prev) => [...prev, url]);
      };
      reader.readAsDataURL(file);
    });

    e.target.value = "";
  };

  const removeExistingImage = async (index: number) => {
    const image = form.existingImages[index];
    if (!window.confirm("Are you sure you want to remove this image?")) return;

    if (editingId) {
      try {
        const response = await projectsApi.deleteImage(editingId, image.id);
        if (response.success) {
          setForm((prev) => ({
            ...prev,
            existingImages: prev.existingImages.filter((_, i) => i !== index),
          }));
          showToast("Image removed successfully");
        } else {
          showToast(response.message || "Failed to remove image", "error");
        }
      } catch {
        showToast("Failed to remove image", "error");
      }
    }
  };

  const removeNewImage = (index: number) => {
    setForm((prev) => ({
      ...prev,
      newFiles: prev.newFiles.filter((_, i) => i !== index),
    }));
    setNewFilePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  if (loading) {
    return (
      <div className="admin-loader">
        <div className="admin-spinner" />
      </div>
    );
  }

  return (
    <div>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Projects</h1>
        <p className="admin-page-subtitle">Manage your portfolio projects</p>
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <span className="admin-card-title">All Projects ({projects.length})</span>
          <button className="admin-btn admin-btn-primary admin-btn-sm" onClick={openCreate}>
            <i className="fa-solid fa-plus" />
            <span>Add Project</span>
          </button>
        </div>

        {projects.length === 0 ? (
          <div className="admin-empty-state">
            <i className="fa-solid fa-folder-open" />
            <p>No projects yet</p>
            <button className="admin-btn admin-btn-primary" onClick={openCreate}>
              <i className="fa-solid fa-plus" />
              <span>Add Your First Project</span>
            </button>
          </div>
        ) : (
          <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Tech Stack</th>
                <th>Images</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.id}>
                  <td style={{ fontWeight: 600, color: "var(--color-text-primary)" }}>
                    {project.name}
                  </td>
                  <td>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "0.3rem" }}>
                      {project.techStack.slice(0, 3).map((tech) => (
                        <span key={tech} className="admin-badge">
                          {tech}
                        </span>
                      ))}
                      {project.techStack.length > 3 && (
                        <span className="admin-badge">+{project.techStack.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td>{project.images.length}</td>
                  <td>
                    <div className="admin-table-actions">
                      <button
                        className="admin-btn admin-btn-secondary admin-btn-sm"
                        onClick={() => openEdit(project)}
                      >
                        <i className="fa-solid fa-pen" />
                      </button>
                      <button
                        className="admin-btn admin-btn-danger admin-btn-sm"
                        onClick={() => handleDelete(project.id)}
                      >
                        <i className="fa-solid fa-trash" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div className="admin-modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h2 className="admin-modal-title">
                {editingId ? "Edit Project" : "New Project"}
              </h2>
              <button className="admin-modal-close" onClick={() => setModalOpen(false)}>
                &times;
              </button>
            </div>

            <div className="admin-modal-body">
              <div className="admin-form-group">
                <label className="admin-form-label">Project Name</label>
                <input
                  type="text"
                  className={`admin-form-input${errors.name ? " admin-input-error" : ""}`}
                  placeholder="e.g. Social Network"
                  value={form.name}
                  onChange={(e) => { setForm((prev) => ({ ...prev, name: e.target.value })); setErrors((prev) => ({ ...prev, name: "" })); }}
                />
                {errors.name && <span className="admin-field-error">{errors.name}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Description (PT)</label>
                <textarea
                  className={`admin-form-textarea${errors.descriptionPt ? " admin-input-error" : ""}`}
                  placeholder="Project description in Portuguese..."
                  value={form.descriptionPt}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, descriptionPt: e.target.value }));
                    setErrors((prev) => ({ ...prev, descriptionPt: "" }));
                  }}
                />
                {errors.descriptionPt && <span className="admin-field-error">{errors.descriptionPt}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Description (EN)</label>
                <textarea
                  className={`admin-form-textarea${errors.descriptionEn ? " admin-input-error" : ""}`}
                  placeholder="Project description in English..."
                  value={form.descriptionEn}
                  onChange={(e) => {
                    setForm((prev) => ({ ...prev, descriptionEn: e.target.value }));
                    setErrors((prev) => ({ ...prev, descriptionEn: "" }));
                  }}
                />
                {errors.descriptionEn && <span className="admin-field-error">{errors.descriptionEn}</span>}
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Tech Stack</label>
                <div className="admin-tags-input">
                  {form.techStack.map((tech) => (
                    <span key={tech} className="admin-tag">
                      {tech}
                      <button
                        className="admin-tag-remove"
                        onClick={() => removeTech(tech)}
                      >
                        &times;
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="admin-tags-field"
                    placeholder="Type and press Enter..."
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyDown={handleTechKeyDown}
                    onBlur={addTech}
                  />
                </div>
                <span className="admin-form-hint">
                  Press Enter or comma to add a technology
                </span>
                {errors.techStack && <span className="admin-field-error">{errors.techStack}</span>}
              </div>

              <div className="admin-form-row">
                <div className="admin-form-group">
                  <label className="admin-form-label">Live URL</label>
                  <input
                    type="text"
                    className={`admin-form-input${errors.viewUrl ? " admin-input-error" : ""}`}
                    placeholder="project.example.com"
                    value={form.viewUrl}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, viewUrl: e.target.value }));
                      setErrors((prev) => ({ ...prev, viewUrl: "" }));
                    }}
                  />
                  {errors.viewUrl && <span className="admin-field-error">{errors.viewUrl}</span>}
                </div>
                <div className="admin-form-group">
                  <label className="admin-form-label">GitHub URL</label>
                  <input
                    type="text"
                    className={`admin-form-input${errors.codeUrl ? " admin-input-error" : ""}`}
                    placeholder="github.com/user/repo"
                    value={form.codeUrl}
                    onChange={(e) => {
                      setForm((prev) => ({ ...prev, codeUrl: e.target.value }));
                      setErrors((prev) => ({ ...prev, codeUrl: "" }));
                    }}
                  />
                  {errors.codeUrl && <span className="admin-field-error">{errors.codeUrl}</span>}
                </div>
              </div>

              <div className="admin-form-group">
                <label className="admin-form-label">Images</label>
                <div className="admin-image-grid">
                  {form.existingImages.map((img, i) => (
                    <div key={`existing-${img.id}`} className="admin-image-item">
                      <img src={img.url} alt={`Project ${i + 1}`} />
                      <button
                        className="admin-image-remove"
                        onClick={() => removeExistingImage(i)}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </div>
                  ))}
                  {newFilePreviews.map((preview, i) => (
                    <div key={`new-${i}`} className="admin-image-item">
                      <img src={preview} alt={`New ${i + 1}`} />
                      <button
                        className="admin-image-remove"
                        onClick={() => removeNewImage(i)}
                      >
                        <i className="fa-solid fa-xmark" />
                      </button>
                    </div>
                  ))}
                  <div
                    className="admin-image-upload-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <i className="fa-solid fa-plus" />
                    <span>Add Image</span>
                  </div>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="admin-file-input"
                  onChange={handleImageUpload}
                />
              </div>
            </div>

            <div className="admin-modal-footer">
              <button
                className="admin-btn admin-btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancel
              </button>
              <button
                className="admin-btn admin-btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                <i className={`fa-solid ${saving ? "fa-spinner fa-spin" : "fa-save"}`} />
                <span>{saving ? "Saving..." : editingId ? "Update" : "Create"}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectsPage;
